'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, Gift, Sparkles } from 'lucide-react';

import { cn } from '@/components/ui/utils';
import { customerFetch, customerUrl, useCustomerSession } from '@/lib/customer-bridge';
import type { TemplateSurfacePalette } from '@/app/dashboard/templates/components/lib/templateExperience';
import { formatMoney as money } from '@/lib/money';

// Wallet-aware moment (doc 08 §7.3 / F2): a signed-in customer browsing a menu
// is reminded, in the place itself, that they have money here -- saved gift
// card balance and linked loyalty points. Read-only awareness: nothing is ever
// applied automatically, the customer still chooses at checkout.

type WalletPayload = {
  ok?: boolean;
  businessId?: string;
  businessName?: string;
  giftCards?: Array<{ giftCardId: string; balance: number; currency: string | null }>;
  loyalty?: {
    businessId: string;
    businessName: string;
    balance: number;
    tier: string | null;
    nextReward: { nextTierName: string; minPoints: number; pointsToNext: number } | null;
  } | null;
};

export function WalletMoment({
  businessId,
  businessName,
  palette,
}: {
  businessId: string;
  businessName?: string | null;
  palette?: TemplateSurfacePalette | null;
}) {
  const { status } = useCustomerSession();
  const [wallet, setWallet] = useState<WalletPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (status !== 'authenticated' || !businessId) { setLoading(false); return; }
    let cancelled = false;
    void customerFetch(`/api/customer-auth/wallet?businessId=${encodeURIComponent(businessId)}`)
      .then(async (res) => {
        try {
          const data = await res.json();
          if (!cancelled && data?.ok) setWallet(data);
        } catch {
          // Malformed response — treat as no wallet data.
        }
      })
      .catch(() => {
        // Awareness is best-effort; a failed lookup must never break the menu.
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
    };
  }, [status, businessId]);

  if (status !== 'authenticated' || loading) return null;

  const giftCards = wallet?.giftCards ?? [];
  const giftTotal = giftCards.reduce((sum, card) => sum + Number(card.balance || 0), 0);
  const giftCurrency = giftCards.find((card) => card.currency)?.currency ?? null;
  const loyalty = wallet?.loyalty ?? null;

  if (!wallet || (giftTotal <= 0 && (!loyalty || loyalty.balance <= 0))) return null;

  const place = wallet.businessName || businessName || 'this place';
  const rows: string[] = [];
  if (giftTotal > 0) rows.push(`You have ${money(giftTotal, giftCurrency)} here.`);
  if (loyalty && loyalty.balance > 0) {
    rows.push(
      loyalty.nextReward
        ? `${loyalty.balance} points here · ${loyalty.nextReward.pointsToNext} to ${loyalty.nextReward.nextTierName}.`
        : `${loyalty.balance} points here.`,
    );
  }

  return (
    <div
      className="mx-auto my-3 flex w-full max-w-[980px] flex-col gap-3 rounded-xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
      style={{
        borderColor: palette?.border,
        backgroundColor: palette?.card,
        color: palette?.cardForeground,
      }}
    >
      <div className="flex items-start gap-2.5">
        <span
          className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: `${palette?.accent || 'hsl(var(--muted))'}22`, color: palette?.accent }}
        >
          <Gift className="size-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 text-sm leading-snug">
          <p className="font-semibold">
            {rows[0]}
            {rows.length > 1 ? <span className="font-normal"> {rows.slice(1).join(' ')}</span> : null}
          </p>
          <p className={cn('mt-0.5 text-xs', 'opacity-70')}>
            {place} · nothing is applied automatically, you choose at checkout.
          </p>
        </div>
      </div>
      <Link
        href={customerUrl('/c/dashboard')}
        className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold underline-offset-4 transition-colors hover:underline"
        style={{ color: palette?.accent }}
      >
        View in your account
        <ArrowRight className="size-3.5" aria-hidden="true" />
      </Link>
    </div>
  );
}
